<script setup lang="ts">
import { MMContainer, MMIcon, MMPopupFullWidth, useMedia } from '@mm-frontend/mm-web-ui-kit';
import { useCategories } from '../business/useCategories';
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import { ref, useTemplateRef } from 'vue';
import DesktopCatalogLink from './DesktopCatalogAsideLink.vue';
import type { CategoryMM } from '~/entities/category';
import CategoryChildrens from './CategoryChildrens.vue';
import { ButtonTertiaryGhost } from '~/features/ui-kit';

const { closeCatalogModal, rootCategories, promoCategories } = useCategories();
const { isTabletAndDown } = useMedia();

const selectedCategory = ref<CategoryMM | null>(null);

const catalogPopup = useTemplateRef<HTMLDivElement>('catalogPopup');
onClickOutside(
  catalogPopup,
  () => {
    closeCatalogModal();
  },
  {
    ignore: ['.catalog-snippet'],
  }
);

watchEffect(() => {
  if (isTabletAndDown.value) {
    closeCatalogModal();
  }

  if (rootCategories.value?.length) {
    selectedCategory.value = rootCategories.value[0];
  }
});

const debouncedSelectCategory = useDebounceFn((category: CategoryMM) => {
  selectedCategory.value = category;
}, 100);
</script>

<template>
  <MMPopupFullWidth class="catalog-popup">
    <MMContainer ref="catalogPopup" class="catalog">
      <div class="catalog__aside">
        <DesktopCatalogLink
          v-for="category in promoCategories"
          :key="category.id"
          :title="category.title"
          :icon-link="category.icon.link"
          :is-promo="true"
          :to="category.url"
        />

        <DesktopCatalogLink
          v-for="category in rootCategories"
          :key="category.id"
          :title="category.title"
          :icon-link="category.icon.link"
          :icon-svg="category.icon.svg"
          :to="category.url"
          :is-selected="selectedCategory?.id === category.id"
          @mouseenter="debouncedSelectCategory(category)"
        />
      </div>

      <div class="catalog__body">
        <CategoryChildrens v-if="selectedCategory" :category="selectedCategory" />
      </div>

      <ButtonTertiaryGhost class="catalog__close-button" round @click="closeCatalogModal">
        <MMIcon name="dismiss-filled" color="tertiary" :height="24" :width="24" />
      </ButtonTertiaryGhost>
    </MMContainer>
  </MMPopupFullWidth>
</template>

<style scoped lang="stylus">
.catalog-popup
  margin-top 8px
  overflow initial

.catalog
  display flex
  gap 16px
  align-items flex-start
  height 100%
  position relative
  padding-bottom 8px

  &__aside
    display flex
    flex-direction column
    width 260px
    padding-right 16px
    border-right 1px solid var(--border-subtle)
    overflow-y auto
    overflow-x visible
    max-height 100%
    margin-left -8px
    box-sizing border-box
    padding-bottom 36px

    & .catalog-link
      flex 1 0 40px

  &__body
    display flex
    flex-direction column
    gap 8px
    flex 1
    padding-top 4px
    padding-left 12px
    overflow-y auto
    max-height 100%
    box-sizing border-box
    padding-bottom 36px

  &__close-button
    position sticky
    top 1px
    cursor pointer
</style>
